
import { useState, useEffect, useMemo } from "react";
import ViewToggle from "./ViewToggle";
import DeviceGrid from "./DeviceGrid";
import DeviceTable from "./DeviceTable";
import MobileDeviceList from "./MobileDeviceList";
import { useDevices } from "@/hooks/useDevices";
import { MonitorSmartphone } from "lucide-react";
import { DeviceListSkeleton } from "./device/DeviceListSkeleton";
import { DeviceSearch } from "./dashboard/DeviceSearch";
import { DeviceStatusCards } from "./dashboard/DeviceStatusCards";
import { CardDisplayToggle, type CardDisplayMode } from "./dashboard/CardDisplayToggle";
import { useScrollResponsiveCards } from "@/hooks/useScrollResponsiveCards";
import { normalizeStatus } from "@/hooks/useDevices";
import { useStaleDeviceThreshold } from "@/hooks/useStaleDeviceThreshold";
import { useIsMobile } from "@/hooks/use-mobile";

interface DeviceListProps {
  statusFilter: string | null;
  onStatusClick: (status: string | null) => void;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
}

const DeviceList = ({ statusFilter, onStatusClick, searchQuery: searchQueryProp, onSearchChange }: DeviceListProps) => {
  const isMobile = useIsMobile();
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("deviceListViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQueryLocal, setSearchQueryLocal] = useState("");
  const isControlled = onSearchChange !== undefined;
  const searchQuery = isControlled ? (searchQueryProp ?? "") : searchQueryLocal;
  const setSearchQuery = isControlled ? onSearchChange! : setSearchQueryLocal;
  const [cardMode, setCardMode] = useState<CardDisplayMode>(() => {
    const savedMode = localStorage.getItem("deviceListCardMode");
    return (savedMode === "full" || savedMode === "compact" || savedMode === "hidden") ? savedMode : "full";
  });
  
  useEffect(() => {
    localStorage.setItem("deviceListViewPreference", view);
  }, [view]);

  useEffect(() => {
    localStorage.setItem("deviceListCardMode", cardMode);
  }, [cardMode]);

  const { currentMode: cardDisplayMode, isSticky, scrollContainerRef, searchBarRef } = useScrollResponsiveCards({
    initialMode: cardMode
  });

  const { data: devices = [], isLoading, error, total, page, totalPages, setPage, deviceTags, statusCounts } = useDevices();

  // Count devices that haven't checked in for the user-configured stale threshold
  const { days: staleThresholdDays } = useStaleDeviceThreshold();
  const staleCount = useMemo(() => {
    const cutoff = Date.now() - staleThresholdDays * 24 * 60 * 60 * 1000;
    return devices.reduce((acc, d) => {
      const ts = d.last_seen || d.created_at;
      if (!ts) return acc;
      const t = new Date(ts).getTime();
      return Number.isFinite(t) && t < cutoff ? acc + 1 : acc;
    }, 0);
  }, [devices, staleThresholdDays]);

  // Phase 5: Use useMemo for expensive computations
  const enrichedDevices = useMemo(() => devices.map(device => {
    return {
      ...device,
      department: device.department || ['Engineering', 'Design', 'Marketing', 'Sales', 'HR', 'Finance'][Math.floor(Math.random() * 6)],
      location: device.location || ['Cupertino HQ', 'New York Office', 'London Office', 'Singapore Office', 'Tokyo Office'][Math.floor(Math.random() * 5)]
    };
  }), [devices]);
  
  // Phase 5: Memoize filtered devices to prevent recalculation on every render
  const filteredDevices = useMemo(() => enrichedDevices.filter((device) => {
    // First check if the device matches the search query
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      (!searchQuery) || // If no search query, include all devices
      (device.name || '').toLowerCase().includes(searchLower) ||
      (device.ip_address || '').toLowerCase().includes(searchLower) ||
      (device.hostname || '').toLowerCase().includes(searchLower) ||
      `${device.os_type || ''} ${device.os_version || ''}`.toLowerCase().includes(searchLower) ||
      (device.serial_number || '').toLowerCase().includes(searchLower) ||
      (device.department || '').toLowerCase().includes(searchLower) ||
      (device.location || '').toLowerCase().includes(searchLower) ||
      (device.customer || '').toLowerCase().includes(searchLower) || // Add search by customer
      (device.tenantId || '').toLowerCase().includes(searchLower) ||  // Add search by tenantId
      (device.username || '').toLowerCase().includes(searchLower) ||
      ((device as any).fullName || '').toLowerCase().includes(searchLower) ||
      (device.userEmail || '').toLowerCase().includes(searchLower);

    // Then check if the device matches the status filter
    const deviceStatus = normalizeStatus(device.status);
    const matchesStatus = !statusFilter || deviceStatus === statusFilter;

    return matchesSearch && matchesStatus;
  }), [enrichedDevices, searchQuery, statusFilter]);


  if (isLoading) {
    return <DeviceListSkeleton />;
  }

  if (error) {
    const isNetworkError = error instanceof TypeError && error.message === 'Failed to fetch';
    const errorMessage = isNetworkError
      ? "Unable to connect to the server. Please check your network connection or try again later."
      : error instanceof Error
        ? error.message
        : "An unexpected error occurred while loading devices.";

    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3 px-6 text-center">
        <p className="text-destructive font-medium">Unable to load devices</p>
        <p className="text-sm text-muted-foreground max-w-md">{errorMessage}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary hover:underline min-h-[44px]"
        >
          Reload page
        </button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-3">
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
          <MonitorSmartphone className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        </div>
        <p className="text-base font-semibold text-foreground">No devices yet</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Enroll your first device to start managing your fleet.
        </p>
      </div>
    );
  }


  return (
    <div ref={scrollContainerRef} className="space-y-4 w-full">
      {/* Card display toggle is desktop-only; on phone we always show the compact card list. */}
      {!isSticky && !isMobile && (
        <div className="flex items-center justify-end">
          <CardDisplayToggle
            mode={cardDisplayMode}
            onModeChange={setCardMode}
          />
        </div>
      )}

        <DeviceStatusCards
          counts={statusCounts}
          isLoading={isLoading}
          onStatusClick={onStatusClick}
          statusFilter={statusFilter}
          mode={isMobile ? "compact" : cardDisplayMode}
          staleCount={staleCount}
          staleThresholdDays={staleThresholdDays}
        />

      <div
        ref={searchBarRef}
        className={`sticky top-0 z-40 -mx-2 px-2 sm:-mx-6 sm:px-6 flex items-center justify-between gap-3 transition-all duration-200 border-b py-3 ${isSticky ? 'bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80 border-border shadow-sm' : 'bg-background border-transparent'}`}
      >
        <div className="flex items-center gap-2 flex-1">
          <DeviceSearch
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
        {/* Grid/list toggle hidden on phone — mobile uses a dedicated card list. */}
        {!isMobile && <ViewToggle view={view} onViewChange={setView} />}
      </div>
      <div className="w-full overflow-visible">
        {isMobile ? (
          <MobileDeviceList devices={filteredDevices} searchQuery={searchQuery} />
        ) : view === "grid" ? (
          <DeviceGrid devices={filteredDevices} searchQuery={searchQuery} />
        ) : (
          <DeviceTable devices={filteredDevices} deviceTags={deviceTags} searchQuery={searchQuery} />
        )}
      </div>

      
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <p className="text-sm text-muted-foreground">
            Showing {devices.length} of {total} devices
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList;
